"use strict";

import { detectOldInterface, detectSsoLanding, loadCookieTokenTuple, loadTokenTuple, resetToken } from 'api/authentic';
import { AuthCheckUserAuth } from 'api/rolesAPICall';
import ApplicationEvents from 'components/applicationEvents';
import { CancelOrdersPanel } from 'components/cancelAllOrders';
import GaijinToolPanel from 'components/gaijinToolPanel';
import { defineSimpleTerm, _I_ } from 'components/locs';
import { DevAuthenticationDialog } from 'components/setup/devAuthenticationDialog';
import { BootButton } from 'generics/buttons';
import { IconGlyphicon } from 'generics/icons';
import { classMixer } from 'generics/utils';
import React from 'react';
import ReactGA from 'react-ga';

LogStream.enable( "LOGIN" );
const loggerAuth = LogStream( "LOGIN" )

export class AuthenticationProcessor extends React.Component  {

  constructor(props) {
    super(props);

    this.state = {
     //  Special case, while sso landing detected we should store token and reload
      ssoLanding        : !!detectSsoLanding(),
      oldInterface      : !!detectOldInterface(props.siteSettings.modernUrl),

      lastUsedToken     : loadTokenTuple(),
      stealAdmToken     : loadCookieTokenTuple(),

      creds             : null,
      authError         : false,
      showAuthDialog    : false,
      tokenExpired      : false,
      notAllowed        : false,
      countryDenied     : false,
    };
  }


  onShowAuthDialog = ()  =>  {
    this.setState({ showAuthDialog:true });
  }


  onHideAuthDialog = ()  =>  {
    this.setState({ showAuthDialog:false });
  }


  onTokenExpired = ()  =>  {
    resetToken();
    this.setState({ tokenExpired:true })
  }


  onShowAuthorizationRequired = ()  =>  {
    this.setState({ authorizationRequired:true })
  }


  onNotAllowed = ( reason )  =>  {
    loggerAuth.warning( "MarketApp::onNotAllowed>>", reason );
    this.setState({ notAllowed:true })
  }

  onCountryDenied = ()  =>  {
    loggerAuth.warning( "MarketApp::onCountryDenied" );
    this.setState({ countryDenied:true })
  }

  onRelogin = ( reason )  =>  {
    const {  siteSettings  } = this.props;
    if ( siteSettings.ssoUrl )  {
      loggerAuth.info( "MarketApp::onRelogin>>", reason );
      document.location.assign( siteSettings.ssoUrl );
    }  else  {
      loggerAuth.error( "MarketApp::onRelogin failed, no SSO enabled" );
    }
  };


  componentDidMount()  {
    ReactGA.pageview('authentication');

    if ( this.state.ssoLanding )
    {
      loggerAuth.info( "SSO Landing detected, waiting for reloading" );
      return;
    }

    ApplicationEvents.on("auth.showDialog", this.onShowAuthDialog);
    ApplicationEvents.on("auth.tokenExpired", this.onTokenExpired);
    ApplicationEvents.on("auth.authorizationRequired", this.onShowAuthorizationRequired);
    ApplicationEvents.on("auth.notAllowed", this.onNotAllowed);
    ApplicationEvents.on("auth.goToRelogin", this.onRelogin);
    ApplicationEvents.on("auth.countryDenied", this.onCountryDenied);

    this.resolveCreds();
  }


  componentWillUnmount()  {
    ApplicationEvents.off("auth.showDialog", this.onShowAuthDialog);
    ApplicationEvents.off("auth.tokenExpired", this.onTokenExpired);
    ApplicationEvents.off("auth.authorizationRequired", this.onShowAuthorizationRequired);
    ApplicationEvents.off("auth.notAllowed", this.onNotAllowed);
    ApplicationEvents.off("auth.goToRelogin", this.onRelogin);
    ApplicationEvents.off("auth.countryDenied", this.onCountryDenied);
  }


  checkToken = (tokenTuple)  =>  {
    tokenTuple = (tokenTuple || {});

    return AuthCheckUserAuth(
        tokenTuple.token
      ).then(
        (creds) => ({ tokenTuple, creds  })
      );
  }


  resolveCreds = ()  =>  {
    loggerAuth.info( "Checking authorization" );
    const { lastUsedToken, stealAdmToken } = this.state;
    this.checkToken(
      lastUsedToken
    ).catch(
      ( err )  =>  ( (stealAdmToken) ? this.checkToken( stealAdmToken ) : Promise.reject(err) )

    ).then(
      this.onCredsResolved,
      this.onAuthError
    );
  }


  onCredsResolved = ({ creds, tokenTuple })  =>  {
    loggerAuth.info( "Got credentials" );
    if ( creds.disallowedByRoles )
      ApplicationEvents.emit_async("auth.notAllowed", "disallowedByRoles");

    if (creds.deniedTags.length > 0)
      ApplicationEvents.emit_async("auth.notAllowed", "deniedTags");

    if ( creds.disallowedByCountry )
      ApplicationEvents.emit_async("auth.countryDenied");

    creds = { ...tokenTuple, ...creds  };
    SettingsInjections.setCredentials( creds );

    this.setState({ lastUsedToken: tokenTuple, creds, });
  }


  onAuthError = ( err )  =>  {
    if ( !this.state.lastUsedToken )  {
      loggerAuth.warning( "onAuthError - showing AuthorizationRequired", err );
      return this.onShowAuthorizationRequired();
    }

    const code = (((err||{}).details||{}).response||{}).error||"Unknown";
    if ( "AUTH_RESPONSE_STATUS_IS_LOGINERROR" == code )  {
      loggerAuth.warning( "onAuthError - showing TokenExpired", err );
      return this.onTokenExpired();
    }

    loggerAuth.warning( "onAuthError - showing AuthError", err );
    this.setState({ authError: code });
  }


  renderDevDialog()  {
    if ( !this.props.siteSettings.devMode )
      return;

    return (
        <DevAuthenticationDialog
              lastUsedToken={(this.state.lastUsedToken||{}).token||""}
              stealAdmToken={(this.state.stealAdmToken||{}).token||""}
              onClose={this.onHideAuthDialog}
            />
      );
  }


  render()  {
    const devDialog = this.renderDevDialog() || null;
    if ( this.state.ssoLanding )  {
      return <LandingMessage devDialog={devDialog} />;
    }

    if ( this.state.tokenExpired )  {
      return <TokenExpiredMessage devDialog={devDialog} />;
    }

    if ( this.state.authorizationRequired )  {
      return <AuthorizationRequiredMessage devDialog={devDialog} />;
    }

    if ( this.state.notAllowed )  {
      return <NotAllowedMessage devDialog={devDialog} />;
    }

    if ( this.state.countryDenied )  {
      return <CountryDeniedMessage devDialog={devDialog} />;
    }

    if ( this.state.authError )  {
      return <AuthErrorMessage  reason={this.state.authError} devDialog={devDialog} />;
    }

    if (  !this.state.creds  )  {
      return <CheckingAuthMessage devDialog={devDialog} />;
    }

    const inlineDevDialog = !!this.state.showAuthDialog && devDialog;
    return (
        <div>
          {  inlineDevDialog   }
          <div key="appHideHolder" className={classMixer(inlineDevDialog && "hidden")} >
            {this.props.children({  credentials: this.state.creds,
                                    devMode: this.props.siteSettings.devMode,  })}
          </div>
        </div>
      );
  }


}


function NotAllowedMessageHandleRelogin()  {
  ApplicationEvents.emit_async("auth.goToRelogin", "NotAllowedMessage.relogin");
}


function NotAllowedMessage({devDialog})  {
  return (
      <GaijinToolPanel type="warning" caption={_I_("NotAllowedMessage.caption")}
            additional={devDialog} >
        <div className="text-center">
          <div className="alert alert-warning" >
            <h1>{_I_("NotAllowedMessage.body.h1")}</h1>
            <h3>{_I_("NotAllowedMessage.body.h3")}</h3>
          </div>
          <div className="text-center">
            <BootButton onClick={NotAllowedMessageHandleRelogin} block >
              <IconGlyphicon type="user" />&nbsp;{_I_("NotAllowedMessage.relogin")}
            </BootButton>
          </div>
        </div>
      </GaijinToolPanel>
    );
}


function CountryDeniedMessage({devDialog}) {
  return (
    <GaijinToolPanel type="warning" caption={_I_("CountryDeniedMessage.caption")}
          additional={devDialog} >
      <div className="text-center">
        <div className="alert alert-danger" >
          <h1>{_I_("CountryDeniedMessage.body.notAllowed")}</h1>
        </div>
        <div className="alert alert-warning" >
          <h4>{_I_("CountryDeniedMessage.body.unclearStatus")}</h4>
        </div>
        <CancelOrdersPanel  />
      </div>
    </GaijinToolPanel>
  );
}


function LandingMessage()  {
  return (
      <GaijinToolPanel type="success" caption={_I_("LandingMessage.caption")} >
        <div className="text-center">
          <div className="alert alert-success" >
            <i className="glyphicon glyphicon-user" />&nbsp;{_I_("LandingMessage.body")}
          </div>
          <i className="fa fa-spinner fa-pulse fa-3x fa-fw" />
        </div>
      </GaijinToolPanel>
    );
}


function TokenExpiredMessageHandleRelogin()  {
  ApplicationEvents.emit_async("auth.goToRelogin", "TokenExpiredMessage.relogin");
}


function TokenExpiredMessage({ devDialog })  {
  return (
      <GaijinToolPanel type="warning" caption={_I_("TokenExpiredMessage.caption")}
            additional={devDialog} >
        <div className="text-center">
          <div className="alert alert-info" >
            <h1>{_I_("TokenExpiredMessage.body.h1")}</h1>
            <h3>{_I_("TokenExpiredMessage.body.h3")}</h3>
          </div>
          <button type="button" className="btn btn-success"  onClick={TokenExpiredMessageHandleRelogin} >
            <i className="glyphicon glyphicon-user" />&nbsp;{_I_("TokenExpiredMessage.relogin")}
          </button>
        </div>
      </GaijinToolPanel>
    );
}


function AuthorizationRequiredMessageHandleRelogin()  {
  ApplicationEvents.emit_async("auth.goToRelogin", "AuthorizationRequiredMessage.relogin");
}


function AuthorizationRequiredMessage({ devDialog })  {
  return (
      <GaijinToolPanel type="default" caption={_I_("AuthorizationRequiredMessage.caption")}
            additional={devDialog} >
        <div className="text-center">
          <div className="alert alert-default" >
            <h3>{_I_("AuthorizationRequiredMessage.body.h3.1")}</h3>
            <h2>{_I_("AuthorizationRequiredMessage.body.h3.2")}</h2>
            <h3>{_I_("AuthorizationRequiredMessage.body.h3.3")}</h3>
          </div>
          <button type="button" className="btn btn-success"  onClick={AuthorizationRequiredMessageHandleRelogin} >
            <i className="glyphicon glyphicon-user" />&nbsp;{_I_("AuthorizationRequiredMessage.login")}
          </button>
        </div>
      </GaijinToolPanel>
    );
}


function AuthErrorMessageHandleRelogin()  {
  ApplicationEvents.emit_async("auth.goToRelogin", "AuthErrorMessage.relogin");
}


function AuthErrorMessage({  reason,  devDialog  })  {
  return (
      <GaijinToolPanel type="warning" caption={_I_("AuthErrorMessage.caption")}
            additional={devDialog} >
        <div className="text-center">
          <div className="alert alert-info" >
            <h1>{_I_("AuthErrorMessage.body.h1")}</h1>
            <div>{_I_("AuthErrorMessage.body.code")} {reason}
              <br/>{_I_("AuthErrorMessage.body.desc")}
            </div>
          </div>
          <button type="button" className="btn btn-success"  onClick={AuthErrorMessageHandleRelogin} >
            <i className="glyphicon glyphicon-user" />&nbsp;{_I_("AuthErrorMessage.relogin")}
          </button>
        </div>
      </GaijinToolPanel>
    );
}


function  CheckingAuthMessage()  {
  return (
    <GaijinToolPanel caption={_I_("CheckingAuthMessage.caption")} >
        <div className="text-center">
          <div className="alert alert-info" >
            <i className="glyphicon glyphicon-hourglass"/>&nbsp;
            {_I_("CheckingAuthMessage.body")}
          </div>
          <i className="fa fa-spinner fa-pulse fa-3x fa-fw" ></i>
        </div>
    </GaijinToolPanel>
    );
}



defineSimpleTerm( "NotAllowedMessage.caption",  "Not allowed",       {
  "ru_RU": "Нет доступа",
  "cs_CZ": "Není povoleno",
  "fr_FR": "Non autorisé",
  "de_DE": "Nicht gestattet",
  "it_IT": "Non permesso",
  "ko_KR": "진행할 수 없습니다",
  "pl_PL": "Niedozwolone",
  "pt_PT": "Not allowed",
  "es_ES": "No permitido",
  "zh_CN": "不允许",
  "zh_TW": "不允許"
} );

defineSimpleTerm( "NotAllowedMessage.body.h1",  "Access to marketplace",       {
  "ru_RU": "Доступ к бирже",
  "cs_CZ": "Přístup na Trh",
  "fr_FR": "Accéder au marché",
  "de_DE": "Zugang zum Marktplatz",
  "it_IT": "Accesso al mercato",
  "ko_KR": "가이진 마켓으로 이동하기",
  "pl_PL": "Dostęp do rynku",
  "pt_PT": "Access to marketplace",
  "es_ES": "Acceso al mercado",
  "zh_CN": "访问交易所",
  "zh_TW": "訪問交易所"
} );

defineSimpleTerm( "NotAllowedMessage.body.h3",  "currently not allowed for your account",       {
  "ru_RU": "закрыт для вашего аккаунта",
  "cs_CZ": "momentálně není vašemu účtu povoleno",
  "fr_FR": "actuellement non autorisé pour votre compte",
  "de_DE": "momentan nicht für ihr Konto gestattet",
  "it_IT": "al momento non permesso sul tuo account",
  "ko_KR": "현재 계정으로 진행할 수 없습니다",
  "pl_PL": "obecnie niedostępne dla Twojego konta",
  "pt_PT": "currently not allowed for your account",
  "es_ES": "actualmente no permitido para tu cuenta",
  "zh_CN": "你的帐户目前无法访问交易所",
  "zh_TW": "你的帳戶目前無法訪問交易所"
} );

defineSimpleTerm( "NotAllowedMessage.relogin",  "Change account",       {
  "ru_RU": "Сменить аккаунт",
  "cs_CZ": "Změnit účet",
  "fr_FR": "Changer de compte",
  "de_DE": "Konto wechseln",
  "it_IT": "Cambia account",
  "ko_KR": "계정 변경",
  "pl_PL": "Zmień konto",
  "pt_PT": "Change account",
  "es_ES": "Cambiar cuenta",
  "zh_CN": "更改帐户",
  "zh_TW": "更改帳戶"
} );

defineSimpleTerm( "CountryDeniedMessage.caption",  "Not allowed",       {
  "ru_RU": "Нет доступа",
  "cs_CZ": "Není povoleno",
  "fr_FR": "Non autorisé",
  "de_DE": "Nicht gestattet",
  "it_IT": "Non permesso",
  "ko_KR": "진행할 수 없습니다",
  "pl_PL": "Niedozwolone",
  "pt_PT": "Not allowed",
  "es_ES": "No permitido",
  "zh_CN": "不允许",
  "zh_TW": "不允許"
} );

defineSimpleTerm( "CountryDeniedMessage.body.notAllowed",  "Access to marketplace is not allowed",       {
  "ru_RU": "Доступ к бирже закрыт",
  "cs_CZ": "Přístup na Trh není povolen",
  "fr_FR": "L'accès au marché n'est pas autorisé",
  "de_DE": "Zugang zum Marktplatz ist nicht gestattet",
  "it_IT": "L'accesso al mercato non è permesso",
  "ko_KR": "가이진 마켓으로 접속할 수 없습니다",
  "pl_PL": "Dostęp do rynku niedostępny",
  "pt_PT": "Access to marketplace is not allowed",
  "es_ES": "El acceso al mercado no está permitido",
  "zh_CN": "不允许访问交易所",
  "zh_TW": "不允許訪問交易所"
} );

defineSimpleTerm( "CountryDeniedMessage.body.unclearStatus",  "Due to the unclear legal status of market transactions in your country, access to the marketplace is disabled",       {
  "ru_RU": "Из-за неясного юридического статуса биржевых транзакций в вашей стране доступ к бирже отключен",
  "cs_CZ": "Kvůli nejasné legislativě transakcí ve vaší zemi je přístup na Trh zamítnut",
  "fr_FR": "En raison du statut juridique non clarifié des transactions au sein du marché dans votre pays, l'accès au marché est désactivé",
  "de_DE": "Aufgrund der unklaren rechtlichen Lage in deinem Land ist der Zugang zum Marktplatz deaktiviert",
  "it_IT": "A causa di uno status legale del tuo Paese riguardanti le transazioni nel mercato, l'accesso al mercato è disabilitato",
  "ko_KR": "온라인 거래에 대한 플레이어 국가의 법적인 문제로 인하여, 가이진 마켓으로 접속할 수 없습니다",
  "pl_PL": "Z uwagi na niejasny status prawny transakcji w twoim kraju, dostęp do Rynku jest wyłączony",
  "pt_PT": "Due to the unclear legal status of market transactions in your country, access to the marketplace is disabled",
  "es_ES": "Debido al estado legal poco claro de las transacciones de mercado en tu país, el acceso al mercado está desactivado",
  "zh_CN": "由于你所在国家/地区的市场交易法律状态不明确，因此访问交易所功能已禁用",
  "zh_TW": "由於你所在國家/地區的市場交易法律狀態不明確，因此訪問交易所功能已禁用"
} );

defineSimpleTerm( "LandingMessage.caption",  "Authentication",       {
  "ru_RU": "Аутентификация",
  "cs_CZ": "Ověření",
  "fr_FR": "Authentification",
  "de_DE": "Authentifizierung",
  "it_IT": "Autenticazione",
  "ko_KR": "본인확인",
  "pl_PL": "Autoryzacja",
  "pt_PT": "Authentication",
  "es_ES": "Autenticación",
  "zh_CN": "验证",
  "zh_TW": "驗證"
} );

defineSimpleTerm( "LandingMessage.body",  "Finishing landing...",       {
  "ru_RU": "Финальные проверки...",
  "cs_CZ": "Dokončuji...",
  "fr_FR": "En cours de connection...",
  "de_DE": "Landung abschließen...",
  "it_IT": "Completamento sbarco...",
  "ko_KR": "마켓으로 접속하는 중..",
  "pl_PL": "Kończenie lądowania...",
  "pt_PT": "Finishing landing...",
  "es_ES": "Acabando...",
  "zh_CN": "正在完成着陆…",
  "zh_TW": "正在完成著陸…"
} );

defineSimpleTerm( "TokenExpiredMessage.caption",  "Authentication expired",       {
  "ru_RU": "Аутентификация истекла",
  "cs_CZ": "Ověření vypršelo",
  "fr_FR": "Authentification expirée",
  "de_DE": "Authentifizierung abgelaufen",
  "it_IT": "Autenticazione scaduta",
  "ko_KR": "본인 확인이 만료되었습니다",
  "pl_PL": "Autoryzacja wygasła",
  "pt_PT": "Authentication expired",
  "es_ES": "Autenticación expirada",
  "zh_CN": "验证已失效",
  "zh_TW": "驗證已失效"
} );

defineSimpleTerm( "TokenExpiredMessage.body.h1",  "Authentication expired",       {
  "ru_RU": "Аутентификация истекла",
  "cs_CZ": "Ověření vypršelo",
  "fr_FR": "Authentification expirée",
  "de_DE": "Authentifizierung abgelaufen",
  "it_IT": "Autenticazione scaduta",
  "ko_KR": "본인 확인이 만료되었습니다",
  "pl_PL": "Autoryzacja wygasła",
  "pt_PT": "Authentication expired",
  "es_ES": "Autenticación expirada",
  "zh_CN": "验证已失效",
  "zh_TW": "驗證已失效"
} );

defineSimpleTerm( "TokenExpiredMessage.body.h3",  "There's not much we can do with this, but you can still",       {
  "ru_RU": "Для продолжения необходимо повторно авторизоваться",
  "cs_CZ": "Moc s tím nenaděláme, ale stále můžete",
  "fr_FR": "Nous ne pouvons pas faire grand chose avec ça, mais vous pouvez quand même continuer",
  "de_DE": "Wir können nicht viel damit anfangen, aber du kannst immer noch",
  "it_IT": "Non c'è molto che possiamo fare con questo, ma puoi sempre provare",
  "ko_KR": "현재로써는 더 이상 진행할 수 없습니다. 더 진행하기 위해서는",
  "pl_PL": "Nie możemy wiele na to poradzić, ale ty możesz nadal",
  "pt_PT": "There's not much we can do with this, but you can still",
  "es_ES": "No hay mucho que podamos hacer con esto, pero aún puedes",
  "zh_CN": "我们能做的不多，但你仍然可以",
  "zh_TW": "我們能做的不多，但你仍然可以"
} );

defineSimpleTerm( "TokenExpiredMessage.relogin",  "Relogin...",       {
  "ru_RU": "Авторизоваться",
  "cs_CZ": "Znovu přihlásit...",
  "fr_FR": "Reconnexion...",
  "de_DE": "Re-Login...",
  "it_IT": "Rilogga...",
  "ko_KR": "재접속...",
  "pl_PL": "Zaloguj ponownie...",
  "pt_PT": "Relogin...",
  "es_ES": "Vuelve a iniciar sesión...",
  "zh_CN": "重新登录…",
  "zh_TW": "重新登錄…"
} );

defineSimpleTerm( "AuthErrorMessage.caption",  "Authentication failed",       {
  "ru_RU": "Не удалось аутентифицироваться",
  "cs_CZ": "Ověření selhalo",
  "fr_FR": "Echec de l'authentification",
  "de_DE": "Authentifizierung gescheitert",
  "it_IT": "Autenticazione fallita",
  "ko_KR": "본인 확인이 만료되었습니다",
  "pl_PL": "Autoryzacja nie powiodła się",
  "pt_PT": "Authentication failed",
  "es_ES": "Autenticación fallida",
  "zh_CN": "验证失败",
  "zh_TW": "驗證失敗"
} );

defineSimpleTerm( "AuthErrorMessage.body.h1",  "Failed to check authentication",       {
  "ru_RU": "Не удалось аутентифицироваться",
  "cs_CZ": "Nepodařilo se provést ověření",
  "fr_FR": "Échec de la vérification de l'authentification",
  "de_DE": "Überprüfung der Authentifizierung gescheitert",
  "it_IT": "Controllo autenticazione fallito",
  "ko_KR": "본인 확인에 실패하였습니다",
  "pl_PL": "Nie udało się autoryzować",
  "pt_PT": "Failed to check authentication",
  "es_ES": "Error al verificar la autenticación",
  "zh_CN": "无法检查验证",
  "zh_TW": "無法檢查驗證"
} );

defineSimpleTerm( "AuthErrorMessage.body.code",  "Code:",       {
  "ru_RU": "Код ошибки:",
  "cs_CZ": "Kód:",
  "fr_FR": "Code:",
  "de_DE": "Code:",
  "it_IT": "Codice:",
  "ko_KR": "코드:",
  "pl_PL": "Kod:",
  "pt_PT": "Code:",
  "es_ES": "Código:",
  "zh_CN": "代码：",
  "zh_TW": "代碼："
} );

defineSimpleTerm( "AuthErrorMessage.body.desc",  "There's not much we can do with this, but you can still",       {
  "ru_RU": "Вы можете попробовать переавторизоваться",
  "cs_CZ": "Moc s tím nenaděláme, ale stále můžete",
  "fr_FR": "Nous ne pouvons pas faire grand chose avec ça, mais vous pouvez quand même continuer",
  "de_DE": "Wir können nicht viel damit anfangen, aber du kannst immer noch",
  "it_IT": "Non c'è molto che possiamo fare con questo, ma puoi sempre provare",
  "ko_KR": "현재로써는 더 이상 진행할 수 없습니다. 더 진행하기 위해서는",
  "pl_PL": "Nie możemy wiele na to poradzić, ale ty możesz nadal",
  "pt_PT": "There's not much we can do with this, but you can still",
  "es_ES": "No hay mucho que podamos hacer con esto, pero aún puedes",
  "zh_CN": "我们能做的不多，但你仍然可以",
  "zh_TW": "我們能做的不多，但你仍然可以"
} );

defineSimpleTerm( "AuthErrorMessage.relogin",  "Relogin...",       {
  "ru_RU": "Авторизоваться",
  "cs_CZ": "Znovu přihlásit...",
  "fr_FR": "Reconnexion...",
  "de_DE": "Re-Login...",
  "it_IT": "Rilogga...",
  "ko_KR": "재접속 해 주십시오...",
  "pl_PL": "Zaloguj ponownie...",
  "pt_PT": "Relogin...",
  "es_ES": "Vuelve a iniciar sesión...",
  "zh_CN": "重新登录…",
  "zh_TW": "重新登錄…"
} );

defineSimpleTerm( "AuthorizationRequiredMessage.caption",  "Authentication required",       {
  "ru_RU": "Требуется аутентификация",
  "cs_CZ": "Vyžadováno ověření",
  "fr_FR": "Authentication requise",
  "de_DE": "Authentifizierung benötigt",
  "it_IT": "Autenticazione richiesta",
  "ko_KR": "본인 확인이 필요합니다",
  "pl_PL": "Wymagana autoryzacja",
  "pt_PT": "Authentication required",
  "es_ES": "Autenticacion requerida",
  "zh_CN": "需要验证",
  "zh_TW": "需要驗證"
} );

defineSimpleTerm( "AuthorizationRequiredMessage.body.h3.1",  "You are about to enter",       {
  "ru_RU": "Для входа на биржу",
  "cs_CZ": "Chystáte se vstoupit",
  "fr_FR": "Vous êtes sur le point d'entrer",
  "de_DE": "Du betrittst jetzt",
  "it_IT": "Stai per entrare",
  "ko_KR": "어서오십시오",
  "pl_PL": "Zaraz wejdziesz",
  "pt_PT": "You are about to enter",
  "es_ES": "Estás a punto de entrar",
  "zh_CN": "你即将进入",
  "zh_TW": "你即將進入"
} );

defineSimpleTerm( "AuthorizationRequiredMessage.body.h3.2",  "Gaijin Marketplace",       {
  "ru_RU": "Биржа Gaijin",
  "cs_CZ": "Gaijin Trh",
  "fr_FR": "Marché Gaijin",
  "de_DE": "Gaijin Marktplatz",
  "it_IT": "Mercato Gaijin",
  "ko_KR": "가이진 마켓",
  "pl_PL": "Rynek Gaijin",
  "pt_PT": "Gaijin Marketplace",
  "es_ES": "Mercado Gaijin",
  "zh_CN": "Gaijin 交易所",
  "zh_TW": "Gaijin 交易所"
} );

defineSimpleTerm( "AuthorizationRequiredMessage.body.h3.3",  "But you need to be authorized to proceed further",       {
  "ru_RU": "требуется авторизация",
  "cs_CZ": "Pro další postup musíte být ověřeni",
  "fr_FR": "Vous devez être autorisé à continuer",
  "de_DE": "Du musst autorisiert sein um fortzufahren",
  "it_IT": "Devi essere autorizzato per procedere",
  "ko_KR": "마켓에 입장하기 위해서는 다음을 진행하여 주십시오",
  "pl_PL": "By przejść dalej musisz autoryzować proces",
  "pt_PT": "But you need to be authorized to proceed further",
  "es_ES": "Pero necesitas estar autorizado para continuar",
  "zh_CN": "但你需要验证才能进一步进行",
  "zh_TW": "但你需要驗證才能進一步進行"
} );

defineSimpleTerm( "AuthorizationRequiredMessage.login",  "Sign-in with Gaijin.Net",       {
  "ru_RU": "Авторизоваться на Gaijin.Net",
  "cs_CZ": "Příhlásit se s Gaijin.Net",
  "fr_FR": "Se connecter avec Gaijin.Net",
  "de_DE": "Mit Gaijin-Net einloggen",
  "it_IT": "Entra con Gaijin.net",
  "ko_KR": "Gaijin.Net에서 계정을 생성하십시오",
  "pl_PL": "Zaloguj się za pomocą Gaijin.Net",
  "pt_PT": "Sign-in with Gaijin.Net",
  "es_ES": "Inicia sesión con Gaijin.net",
  "zh_CN": "通过 Gaijin.Net 登录",
  "zh_TW": "通過 Gaijin.Net 登入"
} );



defineSimpleTerm( "CheckingAuthMessage.caption",  "Authentication",       {
  "ru_RU": "Аутентификация",
  "cs_CZ": "Ověření",
  "fr_FR": "Authentification",
  "de_DE": "Authentifizierung",
  "it_IT": "Autenticazione",
  "ko_KR": "본인확인",
  "pl_PL": "Autoryzacja",
  "pt_PT": "Authentication",
  "es_ES": "Autenticación",
  "zh_CN": "验证",
  "zh_TW": "驗證"
} );

defineSimpleTerm( "CheckingAuthMessage.body",  "Checking authentication, please be patient",       {
  "ru_RU": "Аутентификация, пожалуйста подождите...",
  "cs_CZ": "Kontroluji ověření, chvilku strpení",
  "fr_FR": "Contrôle de l'authentification, soyez patients",
  "de_DE": "Überprüfung der Authentifizierung, bitte warten",
  "it_IT": "Autenticazione in controllo, si prega di attendere",
  "ko_KR": "본인 확인 진행중, 잠시만 기다려주십시오",
  "pl_PL": "Sprawdzanie autoryzacji, prosimy czekać",
  "pt_PT": "Checking authentication, please be patient",
  "es_ES": "Comprobando la autenticación, por favor se paciente",
  "zh_CN": "正在检查验证，请耐心等待",
  "zh_TW": "正在檢查驗證，請耐心等待"
} );


